import { Box, Chip, CircularProgress, Typography } from '@mui/material'
import { GetAnnualPlanTeamAndOptions, SaveAnnualPlanTeam } from '../../../services/AnnualPlanService'
import type { SelectableOption } from '../../../utils/genericTypes'
import { Modal } from '../../customComponents/Modal'
import { useEffect, useState } from 'react'
import MultiUserSelectWithChip from '../../customComponents/MultiUserSelectWithChip'
import type { AnnualPlanTeamMember } from '../../../models/AnnualPlanTeamMember'
import { AnnualPlanRole } from '../../../models/AnnualPlanRole'
import Alert from '@mui/material/Alert'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import type { AnnualPlanTeamSaveRequest } from '../../../models/requestObjects/AnnualPlanRequestObjects'
import type { AnnualPlanTeamOption } from '../../../models/requestObjects/AnnualPlanResponseObjects'
import type { OrganizationOption } from '../../../models/requestObjects/CommonObjects'
import { toast } from 'react-toastify'
import InfoIcon from '@mui/icons-material/Info'
import { handleApiResponse } from '../../../utils/apiHelpers'

interface Props {
  closeModal: () => void
  onSubmit: () => Promise<void>
  annualPlanId: number
  isImplementationPlan?: boolean
}

export const AnnualPlanningTeamModal = (props: Props): JSX.Element => {
  const [planAdministratorOptions, setPlanAdministratorOptions] = useState<AnnualPlanTeamOption[]>([])
  const [planContributorOptions, setPlanContributorOptions] = useState<AnnualPlanTeamOption[]>([])
  const [planContributorsWithOrg, setPlanContributorsWithOrg] = useState<AnnualPlanTeamOption[]>([])
  const [planReviewerOptions, setPlanReviewerOptions] = useState<AnnualPlanTeamOption[]>([])
  const [selectedPlanAdminstrators, setSelectedPlanAdminstrators] = useState<SelectableOption[]>([])
  const [selectedPlanContributors, setSelectedPlanContributors] = useState<SelectableOption[]>([])
  const [selectedPlanReviewers, setSelectedPlanReviewers] = useState<SelectableOption[]>([])
  const [isdsForPlan, setIsdsForPlan] = useState<OrganizationOption[]>([])
  const [isdAlertMessage, setIsdAlertMessage] = useState<string>('')
  const [contributorInactiveMessage, setContributorInactiveMessage] = useState<string>('')
  const [adminInactiveMessage, setAdminInactiveMessage] = useState<string>('')

  const [isLoading, setIsLoading] = useState(true)
  const [inactiveTeamMembers, setInactiveTeamMembers] = useState<number[]>([])

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setIsLoading(true)
        const annualPlanTeamAndOptions = await handleApiResponse(GetAnnualPlanTeamAndOptions(props.annualPlanId))
        if (annualPlanTeamAndOptions == null) return
        setPlanAdministratorOptions(annualPlanTeamAndOptions.allTeamOptions.planAdministrators)
        setPlanReviewerOptions(annualPlanTeamAndOptions.allTeamOptions.planReviewers)
        setPlanContributorsWithOrg(annualPlanTeamAndOptions.allTeamOptions.planContributors)
        setPlanContributorOptions(annualPlanTeamAndOptions.allTeamOptions.planContributors)
        setIsdsForPlan(annualPlanTeamAndOptions.associatedISDs)
        const inactiveMembers: number[] = annualPlanTeamAndOptions.annualPlanTeamMembers.filter(tm => !tm.isActive).map(member => member.userId)
        setInactiveTeamMembers(inactiveMembers)

        const tempPlanAdministrators = getMembersByRole(annualPlanTeamAndOptions.annualPlanTeamMembers, AnnualPlanRole.PlanAdministrator)
        setSelectedPlanAdminstrators(tempPlanAdministrators)
        setAdminInactiveMessage(getAdminInactiveMessage(tempPlanAdministrators, inactiveMembers))

        const tempSelectedPlanContributors = getMembersByRole(annualPlanTeamAndOptions.annualPlanTeamMembers, AnnualPlanRole.PlanContributor)
        setSelectedPlanContributors(tempSelectedPlanContributors)
        setSelectedPlanReviewers(getMembersByRole(annualPlanTeamAndOptions.annualPlanTeamMembers, AnnualPlanRole.PlanReviewer))
        setIsdAlertMessage(getContributorInfoMessage(tempSelectedPlanContributors, annualPlanTeamAndOptions.allTeamOptions.planContributors, annualPlanTeamAndOptions.associatedISDs))
        setContributorInactiveMessage(getContributorInactiveMessage(tempSelectedPlanContributors, inactiveMembers))
      } finally {
        setIsLoading(false)
      }
    }

    void fetchData()
  }, [])

  const getMembersByRole = (team: AnnualPlanTeamMember[], roleName: AnnualPlanRole): SelectableOption[] => {
    return team.filter(member => member.annualPlanRole === roleName).map(member => { return { id: member.userId, name: `${member.firstName} ${member.lastName}` } })
  }

  const handleSubmit = async (): Promise<void> => {
    const saveRequest: AnnualPlanTeamSaveRequest = {
      annualPlanId: props.annualPlanId,
      planContributors: planContributorOptions.filter(pco => selectedPlanContributors.some(spco => spco.id === pco.id)),
      planAdministrators: planAdministratorOptions.filter(pao => selectedPlanAdminstrators.some(spao => spao.id === pao.id)),
      planReviewers: planReviewerOptions.filter(pro => selectedPlanReviewers.some(spro => spro.id === pro.id))
    }

    if (saveRequest.planAdministrators.length === 0) {
      toast.error('Please complete your team before continuing')
      return
    }

    if (saveRequest.planAdministrators.length === 0) {
      toast.error('At least one plan administrator is required.')
      return
    }

    await SaveAnnualPlanTeam(saveRequest)

    await props.onSubmit()
  }

  const onPlanAdministratorsUpdated = (selectedPlanAdminstrators: SelectableOption[]): void => {
    setSelectedPlanAdminstrators(selectedPlanAdminstrators)
    setAdminInactiveMessage(getAdminInactiveMessage(selectedPlanAdminstrators))
  }

  const getContributorInfoMessage = (selectedPlanContributors: SelectableOption[],
    contributorsWithOrg: AnnualPlanTeamOption[],
    isdsOnPlan: OrganizationOption[]): string => {
    let isdsMissing: string[] = []

    if (selectedPlanContributors.length > 0 && contributorsWithOrg.length > 0) {
      const orgIdsSelected = contributorsWithOrg.filter(pcw => selectedPlanContributors.some(item => item.id === pcw.id)).map(contributor => { return contributor.organizationId })
      isdsMissing = isdsOnPlan.filter(isd => !orgIdsSelected?.includes(isd.id)).map(isd => { return isd.name })
    } else {
      isdsMissing = isdsOnPlan.map(isd => { return isd.name })
    }

    let returnMessage = ''
    if (isdsMissing.length > 0) {
      returnMessage = `The following ISDs do not have a contributor assigned yet: ${isdsMissing.join(', ')}`
    }
    return returnMessage
  }

  const getContributorInactiveMessage = (selectedPlanContributors: SelectableOption[], inactiveMembers?: number[]): string => {
    if (selectedPlanContributors.length > 0) {
      const members = inactiveMembers ?? inactiveTeamMembers
      const inactiveExists = selectedPlanContributors.filter(selectedContributor => members.includes(selectedContributor.id))
      if (inactiveExists.length > 0) {
        return 'Please remove inactive plan contributors.'
      }
    }

    return ''
  }

  const getAdminInactiveMessage = (selectedPlanAdminstrators: SelectableOption[], inactiveMembers?: number[]): string => {
    if (selectedPlanAdminstrators.length > 0) {
      const members = inactiveMembers ?? inactiveTeamMembers
      const inactiveExists = selectedPlanAdminstrators.filter(selectedAdmin => members.includes(selectedAdmin.id))
      if (inactiveExists.length > 0) {
        return 'Please remove inactive plan administrators.'
      }
    }

    return ''
  }

  const onPlanContributorsUpdated = (selectedPlanContributors: SelectableOption[]): void => {
    setSelectedPlanContributors(selectedPlanContributors)
    setIsdAlertMessage(getContributorInfoMessage(selectedPlanContributors, planContributorsWithOrg, isdsForPlan))
    setContributorInactiveMessage(getContributorInactiveMessage(selectedPlanContributors))
  }

  return <Modal
    title={props.isImplementationPlan === true ? 'Set Implementation Planning Team' : 'Set Annual Planning Team'}
    open={true}
    data-testid='annualPlanningTeamModal'
    maxWidth='md'
    onClose={props.closeModal}
    onConfirm={handleSubmit}
    buttonClassName='modal-button'
    confirmButtonText='Save Changes'
    confirmButtonClassName='modal-confirm-button'
    cancelButtonText='Cancel'
    bodyContent={ isLoading
      ? <Box data-testid='loadingSpinner' sx={{ display: 'flex', width: '100%', justifyContent: 'center' }}><CircularProgress /></Box>
      : <>
        <Typography variant='h4' sx={{ my: '.75em' }}>Plan Administrators</Typography>
        <Typography variant='body1' sx={{ mb: '.75em' }}>The plan administrators are the RCN team members who are responsible for facilitating the completion of the Contract / Plan.</Typography>
        {
          adminInactiveMessage.length > 0 &&
            <Alert data-testid='adminInactiveAlert' icon={<InfoIcon fontSize="inherit" sx={{ fill: 'var(--grey-900)' }} />} sx={{
              mb: '.5em',
              mt: '.75em',
              backgroundColor: 'var(--yellow-500)',
              color: 'var(--grey-900)',
              fill: 'var(--grey-900)'
            }} severity="warning"
            >{adminInactiveMessage}</Alert>
        }
        <MultiUserSelectWithChip availableOptions={planAdministratorOptions}
          inactiveUserIds={inactiveTeamMembers}
          currentSelection={selectedPlanAdminstrators}
          name='planAdministrators'
          onUpdateSelection={onPlanAdministratorsUpdated}
          labelNoneSelected='Add Plan Administrator'
          labelWithSelected='Add Another Plan Administrator'
          placeholder='Select Plan Administrator'
          dataTestId='planadmin-select'
        ></MultiUserSelectWithChip>

        <Typography variant='h4' sx={{ my: '.75em' }}>Plan Contributors</Typography>
        <Typography variant='body1' sx={{ mb: '.75em' }}>Plan contributors are RCN / ISD team members who are filling out various parts of the plan.</Typography>
        {
          isdAlertMessage.length > 0 &&
            <Alert data-testid='contributorAlert' icon={<InfoOutlinedIcon fontSize="inherit" sx={{ fill: 'white' }} />} sx={{
              mb: '.5em',
              backgroundColor: 'var(--blue-500)',
              color: 'white',
              fill: 'white'
            }} severity="info"
            >{isdAlertMessage}</Alert>
        }
        {
          contributorInactiveMessage.length > 0 &&
            <Alert data-testid='contributorInactiveAlert' icon={<InfoIcon fontSize="inherit" sx={{ fill: 'var(--grey-900)' }} />} sx={{
              mb: '.5em',
              backgroundColor: 'var(--yellow-500)',
              color: 'var(--grey-900)',
              fill: 'var(--grey-900)'
            }} severity="warning"
            >{contributorInactiveMessage}</Alert>
        }
        <MultiUserSelectWithChip availableOptions={planContributorOptions}
          inactiveUserIds={inactiveTeamMembers}
          currentSelection={selectedPlanContributors}
          name='planContributors'
          onUpdateSelection={onPlanContributorsUpdated}
          labelNoneSelected='Add Plan Contributor'
          labelWithSelected='Add Another Plan Contributor'
          placeholder='Select Plan Contributor'
          dataTestId='plancontributor-select'
        ></MultiUserSelectWithChip>

        <Typography variant='h4' sx={{ my: '.75em' }}>Plan Reviewers</Typography>
        <Typography variant='body1' sx={{ mb: '1em' }}>The plan reviewers are members of the START project team who are responsible for final approvals.</Typography>
        {selectedPlanReviewers.map((option) => (
          <Chip
            key={option.id}
            data-testid={`${option.id}chip-${option.id ?? ''}`}
            label={option.name}
            sx={{ color: 'white', backgroundColor: 'var(--blue-500)', fontSize: '14px', my: '.5em', mr: '.25em' }}
          />
        ))}
      </>
    }
  />
}
